import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import '../../styles/tutor.css';
import '../../styles/prepare-up-home.css';
import '../../styles/prepare-up-theory.css';
import '../../styles/prepare-up-case.css';
import APIService from '../../services/APIService';
import { renderMarkdown } from './markdown';
import ModeEmpty from './ModeEmpty';
import LearnPanel, { useLearnOpen } from './LearnPanel';
import { scopeIds } from './memoryLog';
import { buildPaths, getHubTreeCached } from './paths';
import { Bar, HomeSearch, ThemeToggle, buildSearchEntries } from './HomeDashboard';
import { IconArrowLeft, IconBook, IconCheck, IconChevDown, IconInfo, IconMic, IconPerson, IconX } from './icons';

/*
 * CaseStudyMode — an interactive, coached case study.
 *
 * There is no case-study content in the hub, so the case is driven by the
 * app's existing streaming LLM endpoint (/chat — the same one the chat modes
 * and the MCQ fallback use). Each turn asks for strict JSON:
 *   start  → title, scenario, first question, skill tags, 5 case stages
 *   answer → reaction, strengths, things to consider, advance?, follow-up,
 *            a model answer (kept collapsed until the learner has tried)
 *   final  → an end-of-case review
 * Incomplete answers get a probing follow-up instead of the solution; the
 * case moves on when an answer is adequate or after a second attempt.
 */
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const DEFAULT_STAGES = ['Understand the problem', 'Identify stakeholders', 'Analyze and explore', 'Propose a solution', 'Final recommendation'];
const clip = (t, n) => { const s = String(t || '').replace(/\s+/g, ' ').trim(); return s.length > n ? `${s.slice(0, n)}…` : s; };

// Stream /chat and parse the JSON object the model returns.
export async function chatJson(prompt, signal) {
  const resp = await fetch(`${API_BASE_URL}/chat?q=${encodeURIComponent(prompt)}`, { signal });
  if (!resp.ok || !resp.body) throw new Error('Could not reach the AI. Is the backend running?');
  const reader = resp.body.getReader();
  const dec = new TextDecoder();
  let buf = ''; let full = '';
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    const lines = buf.split('\n');
    buf = lines.pop();
    for (const line of lines) {
      if (!line.startsWith('data:')) continue;
      const p = line.slice(5).trim();
      if (!p || p === '[DONE]') continue;
      let t; try { t = JSON.parse(p); } catch { t = p; }
      full += t;
    }
  }
  const s = full.indexOf('{'); const e = full.lastIndexOf('}');
  if (s === -1 || e === -1) throw new Error('The AI returned an unexpected format.');
  return JSON.parse(full.slice(s, e + 1));
}
export const withRetry = async (fn) => { try { return await fn(); } catch (e) { if (e.name === 'AbortError') throw e; return fn(); } };

export const startPrompt = (topics) => `You are an expert interviewer running an interactive CASE STUDY for a learner preparing in: ${topics.join(', ')}.
Create ONE realistic, specific case set in a real company or team that requires applying these topics — decisions, trade-offs and constraints, not trivia.
Return ONLY compact JSON (no markdown fences, no prose) with exactly these keys:
{"title":"case title, max 6 words","scenario":"3-4 sentences: context, goal, constraints","question":"the opening open-ended question: how would they approach it / their first steps","tags":["skill","skill","skill"],"stages":["five short stage names (2-4 words) a strong answer progresses through, e.g. Understand the problem"]}`;

export const turnPrompt = ({ cs, stage, attempt, history, question, message }) => `You are coaching a learner through an interactive case study, like a rigorous but encouraging interviewer. Never hand over the full ideal answer in feedback.
CASE: ${cs.title}
SCENARIO: ${clip(cs.scenario, 700)}
STAGES: ${cs.stages.map((s, i) => `${i + 1}. ${s}`).join('; ')}
CURRENT STAGE: ${stage + 1}. ${cs.stages[stage]} (learner's attempt ${attempt} at this stage)
CONVERSATION SO FAR:
${history.map((h) => `Q: ${clip(h.q, 220)}\nA: ${clip(h.a, 450)}`).join('\n') || '(none)'}
CURRENT QUESTION: ${clip(question, 300)}
LEARNER'S MESSAGE: ${clip(message, 1500)}
If the message is a doubt or clarifying question rather than an attempt, set "type":"clarify" and give a short helpful "reply" (2-3 sentences, no spoilers); other fields may be empty.
Otherwise set "type":"answer" and evaluate it against the CURRENT QUESTION and stage.
Set "advance" true when the answer is adequate for this stage, or when this is attempt 2 or more. If not advancing, "follow_up" must be a probing question nudging toward the most important missing consideration WITHOUT revealing it. If advancing, "follow_up" opens the next stage${stage + 1 >= cs.stages.length ? ' (this is the last stage: leave "follow_up" empty)' : ` (${cs.stages[stage + 1]})`}, building on what the learner said.
Return ONLY compact JSON:
{"type":"answer","reply":"","verdict":"strong|partial|weak","headline":"2-4 word reaction, e.g. Good start!","feedback":"1-2 sentences on what they got right","strengths":["max 3 short items"],"improve":["max 3 short considerations or trade-offs to think about"],"advance":true,"follow_up":"next question","model_answer":"4-6 sentence excellent answer to the CURRENT QUESTION"}`;

export const finalPrompt = ({ cs, history }) => `Give a final review of a learner's performance on a case study.
CASE: ${cs.title}
SCENARIO: ${clip(cs.scenario, 600)}
CONVERSATION:
${history.map((h) => `Q: ${clip(h.q, 200)}\nA: ${clip(h.a, 400)}`).join('\n')}
Return ONLY compact JSON:
{"headline":"2-5 word verdict","summary":"2-3 sentences","score":7,"strengths":["max 3"],"improve":["max 3"],"model_answer":"a concise excellent end-to-end approach, 5-7 sentences"}
"score" is an integer from 0 to 10.`;

const Collapse = ({ icon, title, children, tone = '' }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className={`cs-collapse${open ? ' open' : ''}`}>
      <button type="button" className="cs-collapse-btn" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
        <span className={`cs-collapse-icon ${tone}`} aria-hidden="true">{icon}</span>
        <span>{title}</span>
        <IconChevDown size={16} />
      </button>
      {open && <div className="cs-collapse-body">{children}</div>}
    </div>
  );
};

const CaseStudyMode = ({
  config, onConfigure,
  onBack, profile, onEditProfile, theme, onToggleTheme, onOpenPath, onOpenHubNode,
}) => {
  const cids = useMemo(() => scopeIds(config), [config]);
  const topics = useMemo(() => (config?.subjects || []).map((s) => s.name).filter(Boolean), [config]);
  const storeKey = `pu_case_${cids.join('|')}`;
  const saved = useMemo(() => { try { return JSON.parse(sessionStorage.getItem(storeKey) || 'null'); } catch { return null; } }, [storeKey]);

  const [cs, setCs] = useState(saved?.cs || null);                 // { title, scenario, question, tags, stages }
  const [items, setItems] = useState(saved?.items || []);            // the conversation
  const [stage, setStage] = useState(saved?.stage || 0);
  const [attempt, setAttempt] = useState(saved?.attempt || 1);
  const [question, setQuestion] = useState(saved?.question || '');   // the question awaiting an answer
  const [history, setHistory] = useState(saved?.history || []);      // [{ q, a }]
  const [review, setReview] = useState(saved?.review || null);       // latest strengths / improve / model answer
  const [finished, setFinished] = useState(saved?.finished || false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [input, setInput] = useState('');
  const [asideOpen, setAsideOpen] = useState(false);                 // the side panel on narrow screens
  const [learnOpen, toggleLearn] = useLearnOpen('case');
  const [hub, setHub] = useState(null);
  const abortRef = useRef(null);
  const endRef = useRef(null);
  const startedRef = useRef(false);

  useEffect(() => {
    let live = true;
    getHubTreeCached().then((d) => { if (live) setHub(d.tree || []); }).catch(() => { if (live) setHub([]); });
    return () => { live = false; };
  }, []);
  const searchEntries = useMemo(() => {
    const p = hub ? buildPaths(hub) : null;
    return buildSearchEntries(hub, p ? [...p.roles, ...p.areas] : []);
  }, [hub]);

  // Persist each settled step so leaving the page loses nothing.
  useEffect(() => {
    if (!cs || busy) return;
    try { sessionStorage.setItem(storeKey, JSON.stringify({ cs, items, stage, attempt, question, history, review, finished })); } catch { /* quota */ }
  }, [cs, items, stage, attempt, question, history, review, finished, busy, storeKey]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' }); }, [items, busy]);
  useEffect(() => () => abortRef.current?.abort(), []);

  const begin = useCallback(async () => {
    abortRef.current?.abort();
    abortRef.current = new AbortController();
    setBusy(true); setErr('');
    setCs(null); setItems([]); setStage(0); setAttempt(1); setHistory([]); setReview(null); setFinished(false); setQuestion('');
    try {
      const r = await withRetry(() => chatJson(startPrompt(topics), abortRef.current.signal));
      const stages = Array.isArray(r.stages) && r.stages.length >= 3 ? r.stages.slice(0, 5).map((x) => clip(x, 32)) : DEFAULT_STAGES;
      const next = {
        title: clip(r.title || 'Case study', 60), scenario: r.scenario || '', question: r.question || 'How would you approach this?',
        tags: (r.tags || []).slice(0, 3).map((t) => clip(t, 28)), stages,
      };
      setCs(next);
      setQuestion(next.question);
      setItems([{ kind: 'scenario' }]);
    } catch (e) {
      if (e.name !== 'AbortError') setErr(e.message || 'Could not start the case.');
    } finally { setBusy(false); }
  }, [topics]);

  useEffect(() => {
    if (!cids.length || startedRef.current) return;
    startedRef.current = true;
    if (!saved?.cs) begin();
  }, [cids.length]);   // eslint-disable-line react-hooks/exhaustive-deps

  const send = useCallback(async () => {
    const text = input.trim();
    if (!text || busy || !cs || finished) return;
    setInput(''); setErr('');
    setItems((l) => [...l, { kind: 'user', text }]);
    abortRef.current?.abort();
    abortRef.current = new AbortController();
    setBusy(true);
    try {
      const r = await withRetry(() => chatJson(turnPrompt({ cs, stage, attempt, history, question, message: text }), abortRef.current.signal));
      if (r.type === 'clarify') {
        setItems((l) => [...l, { kind: 'ai', text: r.reply || 'Good question — think about it in terms of the scenario’s goal and constraints.' }]);
        return;
      }
      const nextHistory = [...history, { q: question, a: text }];
      setHistory(nextHistory);
      setReview({
        strengths: (r.strengths || []).slice(0, 3), improve: (r.improve || []).slice(0, 3),
        model: r.model_answer || '', for: question,
      });
      setItems((l) => [...l, {
        kind: 'feedback', verdict: r.verdict || 'partial', headline: r.headline || 'Thanks — let’s build on that.',
        text: r.feedback || '', consider: (r.improve || []).slice(0, 3),
      }]);
      const last = stage + 1 >= cs.stages.length;
      if (r.advance && last) {
        // Case complete → final review.
        const fr = await withRetry(() => chatJson(finalPrompt({ cs, history: nextHistory }), abortRef.current.signal));
        setStage(cs.stages.length);
        setFinished(true);
        setReview({ strengths: (fr.strengths || []).slice(0, 3), improve: (fr.improve || []).slice(0, 3), model: fr.model_answer || '', for: 'the whole case' });
        setItems((l) => [...l, { kind: 'final', headline: fr.headline || 'Case complete', summary: fr.summary || '', score: Math.max(0, Math.min(10, Number(fr.score) || 0)) }]);
        return;
      }
      const follow = r.follow_up || (r.advance ? `Let’s move on: ${cs.stages[stage + 1]}. What would you do?` : 'What else would you consider before deciding?');
      if (r.advance) { setStage((s) => s + 1); setAttempt(1); } else { setAttempt((a) => a + 1); }
      setQuestion(follow);
      setItems((l) => [...l, { kind: 'followup', text: follow, stageName: r.advance ? cs.stages[stage + 1] : null }]);
    } catch (e) {
      if (e.name !== 'AbortError') setErr(e.message || 'Could not review that answer.');
    } finally { setBusy(false); }
  }, [input, busy, cs, finished, stage, attempt, history, question]);

  // Dictation, as on Theory: speech lands in the same input.
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const toggleMic = useCallback(async () => {
    if (recording) { recRef.current?.stop(); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setRecording(false); setTranscribing(true);
        try {
          const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
          const b64 = await new Promise((res) => { const fr = new FileReader(); fr.onloadend = () => res(String(fr.result).split(',')[1]); fr.readAsDataURL(blob); });
          const t = await APIService.transcribeAudio(b64);
          if (t && t.transcription) setInput((a) => (a ? `${a} ` : '') + t.transcription);
        } catch { /* ignore */ } finally { setTranscribing(false); }
      };
      recRef.current = rec; rec.start(); setRecording(true);
    } catch { /* mic unavailable */ }
  }, [recording]);

  if (!cids.length) {
    return <ModeEmpty title="No topics configured" body="Choose topics and a case study will be built around them." scene="theory" eyebrow="Case study" onConfigure={onConfigure} />;
  }

  const displayName = profile?.display_name || profile?.name || '';
  const initials = displayName.split(/\s+/).filter(Boolean).map((w) => w[0]).slice(0, 2).join('').toUpperCase();
  const stages = cs?.stages || DEFAULT_STAGES;
  const shownStage = Math.min(stage + 1, stages.length);
  const triedCurrent = review && (finished || history.length > 0);

  const aside = (
    <aside className={`cs-aside${asideOpen ? ' open' : ''}`} aria-label="Case progress and review">
      <div className="pu-panel cs-progress">
        <div className="cs-progress-head">
          <h2>Case progress</h2>
          <span>{finished ? `${stages.length}/${stages.length}` : `${shownStage}/${stages.length}`}</span>
          <button type="button" className="fx-close cs-aside-close" onClick={() => setAsideOpen(false)} aria-label="Close panel"><IconX size={16} /></button>
        </div>
        <div className="cs-segs" aria-hidden="true">
          {stages.map((s, i) => <i key={s} className={finished || i < stage ? 'done' : i === stage ? 'cur' : ''} />)}
        </div>
        <ol className="cs-stages">
          {stages.map((s, i) => {
            const state = finished || i < stage ? 'done' : i === stage ? 'cur' : '';
            return (
              <li key={s} className={state} aria-current={state === 'cur' ? 'step' : undefined}>
                <span className="cs-stage-num">{state === 'done' ? <IconCheck size={12} /> : i + 1}</span>
                <span>{s}</span>
              </li>
            );
          })}
        </ol>
      </div>
      <div className="pu-panel cs-tips">
        <span className="cs-tips-icon" aria-hidden="true"><IconInfo size={16} /></span>
        <div>
          <b>Tips</b>
          <p>Think out loud, structure your answer, and consider trade-offs. You can also ask a doubt in the box below.</p>
        </div>
      </div>
      <div className="pu-panel cs-review">
        <h2>Review &amp; feedback</h2>
        {review ? (
          <>
            <Collapse icon={<IconCheck size={13} />} tone="ok" title="Strengths">
              {review.strengths.length ? <ul>{review.strengths.map((s) => <li key={s}>{s}</li>)}</ul> : <p>Nothing specific yet.</p>}
            </Collapse>
            <Collapse icon="!" tone="warn" title="Areas to improve">
              {review.improve.length ? <ul>{review.improve.map((s) => <li key={s}>{s}</li>)}</ul> : <p>Nothing major — keep going.</p>}
            </Collapse>
            {triedCurrent && review.model && (
              <Collapse icon={<IconBook size={13} />} title="Model answer">
                <p className="cs-model-for">For: {clip(review.for, 120)}</p>
                <div className="cs-model">{renderMarkdown(review.model)}</div>
              </Collapse>
            )}
          </>
        ) : <p className="pu-meta">Answer the first question to see strengths, areas to improve and a model answer.</p>}
      </div>
    </aside>
  );

  return (
    <div className="pu-home pu-tutor pu-case">
      <div className="pu-tutor-top">
        <header className="pu-topbar">
          <HomeSearch entries={searchEntries}
            onPick={(e) => (e.kind === 'track' ? onOpenPath?.(e.id) : onOpenHubNode?.(e.id))} />
          <div className="pu-topbar-end">
            {onToggleTheme && <ThemeToggle theme={theme} onToggle={onToggleTheme} />}
            <button type="button" className="pu-avatar" onClick={onEditProfile}
              aria-label={displayName ? `${displayName} — profile` : 'Profile'}>
              {profile?.avatar ? <img src={profile.avatar} alt="" /> : <span>{(displayName || 'U').slice(0, 1).toUpperCase()}</span>}
            </button>
          </div>
        </header>
        <section className="tt-head" aria-labelledby="cs-title">
          <div className="tt-head-main">
            {onBack && (
              <button type="button" className="pu-link tt-back" onClick={onBack}>
                <IconArrowLeft size={13} /> Back to Practice
              </button>
            )}
            <p className="pu-kicker">Case study</p>
            <h1 id="cs-title">{cs?.title || (busy ? 'Building your case…' : 'Case study')}</h1>
            <p className="tt-sub">Work through a real-world scenario and build your solution step by step.</p>
          </div>
          <div className="cs-head-actions">
            <button type="button" className="tt-learn cs-progress-toggle" onClick={() => setAsideOpen(true)} aria-expanded={asideOpen}>
              Progress {finished ? stages.length : shownStage}/{stages.length}
            </button>
            <button type="button" className={`tt-learn${learnOpen ? ' on' : ''}`} onClick={toggleLearn} aria-pressed={learnOpen}
              title={learnOpen ? 'Hide the learn page' : 'Read the concept notes beside your practice'}>
              <IconBook size={15} /> Learn
            </button>
          </div>
        </section>
      </div>

      <div className="tu-wrap cs-wrap">
        {learnOpen && (
          <LearnPanel conceptId={cids[0]} onClose={toggleLearn}
            conceptOptions={(config?.subjects || []).map((x) => ({ id: x.id, name: x.name }))} />
        )}
        <div className="tu-main">
          <div className="tu-transcript">
            {!cs && busy && <div className="tu-thinking"><span /><span /><span /> Building a case around {topics.join(', ')}…</div>}
            {items.map((it, n) => {
              const prev = items[n - 1];
              const cont = !!prev && it.kind !== 'user' && prev.kind !== 'user';
              if (it.kind === 'user') {
                return (
                  <div key={n} className="tu-msg user">
                    <span className="tu-avatar" aria-hidden="true">{initials || <IconPerson size={16} />}</span>
                    <div className="tu-msgcol"><div className="tu-msgbody">{it.text}</div></div>
                  </div>
                );
              }
              const body = it.kind === 'scenario' ? (
                <div className="tu-card cs-card">
                  <h3>Scenario</h3>
                  <p className="cs-text">{cs.scenario}</p>
                  <p className="cs-q">{cs.question}</p>
                  {cs.tags.length > 0 && <div className="tu-tags">{cs.tags.map((t) => <span key={t} className="tu-tag-pill">{t}</span>)}</div>}
                </div>
              ) : it.kind === 'feedback' ? (
                <div className={`tu-card cs-card cs-fb ${it.verdict}`}>
                  <h3>{it.headline}</h3>
                  {it.text && <p className="cs-text">{it.text}</p>}
                  {it.consider.length > 0 && (
                    <>
                      <p className="cs-consider">To strengthen your approach, consider:</p>
                      <ul className="cs-list">{it.consider.map((c) => <li key={c}>{c}</li>)}</ul>
                    </>
                  )}
                </div>
              ) : it.kind === 'followup' ? (
                <div className="tu-card cs-card">
                  <h3>{it.stageName ? `Next: ${it.stageName}` : 'Follow-up question'}</h3>
                  <p className="cs-q plain">{it.text}</p>
                </div>
              ) : it.kind === 'final' ? (
                <div className="tu-card cs-card cs-final">
                  <p className="pu-kicker">Case complete</p>
                  <h3>{it.headline}</h3>
                  <div className="pu-goal-bar"><Bar value={it.score / 10} label={`Score ${it.score} out of 10`} /><b>{it.score}/10</b></div>
                  {it.summary && <p className="cs-text">{it.summary}</p>}
                  <p className="pu-meta">Strengths, areas to improve and a model approach are in Review &amp; feedback.</p>
                  <div className="cs-final-actions">
                    <button type="button" className="tu-primary" onClick={begin}>Start a new case</button>
                    {onBack && <button type="button" className="tu-ghost" onClick={onBack}>Back to Practice</button>}
                  </div>
                </div>
              ) : (
                <div className="tu-msgbody">{it.text}</div>
              );
              return (
                <div key={n} className={`tu-slot-main${cont ? ' cont' : ''}`}>
                  <span className="tu-avatar" aria-hidden="true">✦</span>
                  {body}
                </div>
              );
            })}
            {busy && cs && <div className="tu-thinking small"><span /><span /><span /></div>}
            {err && (
              <div className="cs-err" role="alert">
                {err} <button type="button" className="pu-link" onClick={cs ? () => setErr('') : begin}>{cs ? 'Dismiss' : 'Try again'}</button>
              </div>
            )}
            <div ref={endRef} />
          </div>

          <div className="tu-composer">
            <textarea
              className="tu-composer-in"
              placeholder={finished ? 'Case complete — start a new case above.' : 'Type your answer here, or ask a doubt… (Enter to send)'}
              aria-label="Your answer or question"
              value={input}
              disabled={busy || !cs || finished}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
              rows={1}
            />
            <button type="button" className={`tu-mic${recording ? ' rec' : ''}`} onClick={toggleMic}
              disabled={transcribing || busy || !cs || finished}
              aria-label={recording ? 'Stop recording' : 'Dictate your answer'}>
              {recording ? '■' : transcribing ? '…' : <IconMic size={16} />}
            </button>
            <button type="button" className="tu-primary tu-send" disabled={busy || !input.trim() || finished} onClick={send}>
              Send
            </button>
          </div>
        </div>
        {asideOpen && <div className="cs-scrim" onClick={() => setAsideOpen(false)} aria-hidden="true" />}
        {aside}
      </div>
    </div>
  );
};

export default CaseStudyMode;
